import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TherapyModality {
  id: string;
  name_en: string;
  name_cn: string;
  mechanism_en: string | null;
  mechanism_cn: string | null;
  procedures: string | null;
  session_length_minutes: number | null;
  recommended_number_of_sessions: number | null;
  certifications: string | null;
  organizations: string | null;
  evidence_level: string | null;
  stream_type: string | null;
  type: string | null;
  target_domain: string | null;
  subcategories: string[] | null;
  stage_0: boolean;
  stage_1: boolean;
  stage_2: boolean;
  stage_3: boolean;
  stage_4: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface HealingStage {
  id: number;
  stage_number: number;
  title_en: string | null;
  title_zh: string | null;
  subtitle_en: string | null;
  subtitle_zh: string | null;
  description: string | null;
  color_theme: string | null;
  is_active: boolean | null;
}

export const useTherapyModalities = () => {
  return useQuery({
    queryKey: ['therapy-modalities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_modalities')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name_en', { ascending: true });

      if (error) throw error;
      return data as TherapyModality[];
    },
  });
};

export const useHealingStages = () => {
  return useQuery({
    queryKey: ['healing-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('healing_stages')
        .select('*')
        .eq('is_active', true)
        .order('stage_number', { ascending: true });

      if (error) throw error;
      return data as HealingStage[];
    },
  });
};

// Get therapies grouped by stage
export const getTherapiesByStage = (therapies: TherapyModality[], stageNumber: number): TherapyModality[] => {
  const stageKey = `stage_${stageNumber}` as keyof TherapyModality;
  return therapies.filter(t => t[stageKey] === true);
};

// Get unique evidence levels
export const getEvidenceLevels = (therapies: TherapyModality[]): string[] => {
  const levels = new Set<string>();
  therapies.forEach(t => {
    if (t.evidence_level) levels.add(t.evidence_level);
  });
  return Array.from(levels).sort();
};

// Get unique stream types
export const getStreamTypes = (therapies: TherapyModality[]): string[] => {
  const types = new Set<string>();
  therapies.forEach(t => {
    if (t.stream_type) types.add(t.stream_type);
  });
  return Array.from(types).sort();
};
